import type { RuleDefault } from './types.ts';

// House rules Matt and Nick should agree on before Arlo comes home. Puppies learn
// fastest when everyone does the same thing, so the answer matters less than
// agreeing on it. These are sensible starting points based on current UK
// welfare guidance (RSPCA, Dogs Trust, Battersea, Blue Cross, RKC).

export const RULES: RuleDefault[] = [
  {
    id: 'sofa',
    question: "Is he allowed on the sofa?",
    suggestion: "Either is fine, but decide now. A good middle ground: only by invitation, on a blanket, and he gets off happily when asked (“Off”, then a treat on the floor). Lift him on and off while he’s small rather than letting him jump.",
  },
  {
    id: 'our-bed',
    question: "Is he allowed on our bed?",
    suggestion: "Not while he’s a puppy. He needs to learn to sleep in his own crate or bed, and jumping down from a height isn’t good for growing joints. You can always invite him up later if you both want to.",
  },
  {
    id: 'upstairs',
    question: "Is he allowed upstairs?",
    suggestion: "No stairs before 12 weeks (RKC advice), and daily stair use when very young has been linked to hip problems. Use a baby gate at the bottom and carry him if he needs to go up. Decide later whether upstairs is part of his territory as an adult.",
  },
  {
    id: 'night-sleep',
    question: "Where does he sleep at night?",
    suggestion: "In a covered crate, next to one of our beds for the first couple of weeks, then gradually moved to where he’ll sleep long term if that’s different. Puppies who slept in a crate or closed room overnight had fewer separation problems later (Generation Pup). Never leave him to cry it out.",
  },
  {
    id: 'crate-day',
    question: "How do we use the crate in the day?",
    suggestion: "For naps, with the door closed once he’s happy in there. After 45–60 minutes awake, pop him in with a chew for a sleep. Never as a punishment, always with water, and never for longer than he can comfortably hold his bladder.",
  },
  {
    id: 'night-shifts',
    question: "Who gets up with him at night?",
    suggestion: "Take turns, a night each, so you’re both human. Expect 1–2 wee trips a night at 8–12 weeks. Keep them dull: lead on, out to the toilet spot, “Be quick”, a quiet “Yes!” and treat, then straight back to bed. Log it so you can see the gaps growing.",
  },
  {
    id: 'table-feeding',
    question: "Is he fed from the table or while we cook?",
    suggestion: "Never from the table, never while you’re cooking, and never from your plate. Feeding at the table creates a begging dog for life. Give him a stuffed Kong or a chew on his bed while you eat instead.",
  },
  {
    id: 'scraps',
    question: "Can he have human food scraps?",
    suggestion: "Mostly no. Treats (including human food) should be under 10% of his daily food, and many foods are dangerous: grapes, raisins, chocolate, onions, xylitol and cooked bones. Small bits of plain cooked chicken or carrot, in his bowl or as training rewards, are fine.",
  },
  {
    id: 'visitors',
    question: "How do we handle visitors?",
    suggestion: "He’s on a lead or behind a baby gate when people arrive, until he’s calm. Visitors ignore him until four paws are on the floor, then crouch to say hello. Children always supervised, and nobody picks him up without asking.",
  },
  {
    id: 'jumping',
    question: "Is jumping up ever okay?",
    suggestion: "No, not even when he’s small and cute, and not even when we’re in scruffy clothes. It’s impossible for him to tell good clothes from bad. Four on the floor gets attention; jumping gets arms folded and a turned back. Use “Off”, not “Down”.",
  },
  {
    id: 'rough-play',
    question: "What are the rules for rough play and tug?",
    suggestion: "Tug is great, with rules: he lets go on “Drop” for a treat, then the game restarts. Teeth on skin means the game pauses for a few seconds. No wrestling or chasing him around the house, which teaches him to be chased and bite harder.",
  },
  {
    id: 'his-toys',
    question: "Which things are his to chew and carry?",
    suggestion: "His toys and chews only, kept in his own box. No old shoes, socks or slippers as toys: he can’t tell old from new. Keep shoes, socks, remotes and cables out of reach, and if he picks something up, trade it for a treat rather than chasing him.",
  },
  {
    id: 'bedroom-doors',
    question: "Do bedroom and bathroom doors stay shut?",
    suggestion: "Yes, while he’s a puppy. Bedrooms and bathrooms are full of socks, pants, tissues, medicines and cables. Closed doors keep him safe and make toilet training easier, as there are fewer hidden corners for accidents.",
  },
  {
    id: 'garden-alone',
    question: "Can he go out to the toilet with just one of us, or wander the flat unsupervised?",
    suggestion: "No, not as a puppy. One of us always goes out with him, so we can reward toileting and stop him eating slugs, snails, stones, bulbs or plants. Check the fences and gates for gaps at puppy height before he comes home.",
  },
  {
    id: 'car',
    question: "How does he travel in the car?",
    suggestion: "Always restrained: in a secured crate in the boot or on the back seat, or a crash-tested car harness clipped to the seat belt point. The Highway Code (rule 57) requires it. Never in the front footwell or loose in the car, and lift him in and out while he’s young.",
  },
  {
    id: 'lead-kit',
    question: "Harness or collar for walks?",
    suggestion: "Collar with his ID tag on at all times in public (it’s the law, even with a microchip). Clip the lead to a well-fitting Y-front harness for walks and always use a harness with a long line. This protects his neck while he learns not to pull.",
  },
  {
    id: 'nipping',
    question: "What do we both do when he nips?",
    suggestion: "The same thing, every time: stop, go still and quiet for a few seconds, then offer a toy to bite instead. If he’s frantic and bitey, he’s usually overtired, so it’s nap time in the crate with a chew. No shouting, tapping his nose or holding his mouth.",
  },
  {
    id: 'alone-plan',
    question: "How do we build up alone time?",
    suggestion: "Start with seconds behind a baby gate in the first week and build up very slowly, only increasing when he stays relaxed. Use the camera to check. Arrive and leave calmly. Plan so he’s never left longer than he can cope with; adult dogs shouldn’t be left more than 4 hours, puppies much less.",
  },
  {
    id: 'treat-ration',
    question: "Where do training treats come from?",
    suggestion: "From his daily ration. Weigh his food in the morning and take training treats from it, plus a few special high-value treats for recall and trickier work. Treats shouldn’t be more than 10% of his daily food. Cockers put on weight easily.",
  },
  {
    id: 'rota',
    question: "Who does which meals, walks and jobs?",
    suggestion: "Agree a simple rota and put it on the tablet: who does breakfast and the first wee, lunch, the evening meal and last wee, and which walks. Share training too, so he listens to both of you, and log everything so neither of you double-feeds him.",
  },
];
